import { Fuel, Gauge, Check } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { useTheme } from "@/contexts/ThemeContext";
import { vehiclePrimaryImage } from "@/utils/vehicleImages";
import { formatVehiclePrice } from "@/utils/vehiclePrice";

export default function VehicleCard({ vehicle, onClick, isSelected, onToggleCompare, showCompare = false }) {
  const { t, language } = useTranslation();
  const { isDark } = useTheme();
  const primaryImage = vehiclePrimaryImage(vehicle);

  // Exterior color: prefer save-time Google Translate field in Spanish mode
  const displayExteriorColor =
    language === "es" &&
    typeof vehicle.exterior_color_es === "string" &&
    vehicle.exterior_color_es.trim()
      ? vehicle.exterior_color_es.trim()
      : vehicle.exterior_color || "";

  // Fuel type: hardcoded i18n labels (same map as vehicle modal / inventory filters)
  const displayFuelType = (() => {
    const raw = vehicle.fuel_type;
    if (raw == null || raw === "") return raw;
    const key = String(raw).trim().toLowerCase().replace(/[-_]+/g, " ").replace(/\s+/g, " ");
    const map = {
      gasoline: "vehicleModal.values.fuelGasoline",
      gas: "vehicleModal.values.fuelGasoline",
      petrol: "vehicleModal.values.fuelGasoline",
      electric: "vehicleModal.values.fuelElectric",
      diesel: "vehicleModal.values.fuelDiesel",
      hybrid: "vehicleModal.values.fuelHybrid",
    };
    const path = map[key];
    if (!path) return raw;
    const translated = t(path);
    return translated.startsWith("vehicleModal.values.") ? raw : translated;
  })();

  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    if (onToggleCompare) {
      onToggleCompare(vehicle);
    }
  };

  return (
    <div
      onClick={onClick}
      className={`group relative overflow-hidden cursor-pointer transition-all duration-300 card-lift ${
        isDark 
          ? 'bg-[#0A0A0A]' 
          : 'bg-white shadow-md hover:shadow-xl'
      } ${
        isSelected 
          ? isDark 
            ? 'border border-blue-500 ring-2 ring-blue-500/30' 
            : 'border-2 border-red-500 ring-2 ring-red-500/30'
          : isDark 
            ? 'border border-white/10 hover:border-blue-500/50' 
            : 'border border-gray-200 hover:border-red-300'
      }`}
      data-testid={`vehicle-card-${vehicle.id}`}
    >
      {/* Image Container */}
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={primaryImage}
          alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
          className="w-full h-full object-cover image-zoom"
          loading="lazy"
        />
        
        {/* Overlay gradient */}
        <div className={`absolute inset-0 opacity-70 ${
          isDark 
            ? 'bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent' 
            : 'bg-gradient-to-t from-black/60 via-transparent to-transparent'
        }`} />
        
        {/* Status Badge */}
        <div className="absolute top-5 left-5">
          <span className={`px-4 py-1.5 text-xs font-semibold uppercase tracking-wider ${
            vehicle.status === "available" 
              ? "bg-emerald-500/90 text-white" 
              : "bg-red-500/90 text-white"
          }`}>
            {vehicle.status === "available" ? t('featured.available') : t('featured.sold')}
          </span>
        </div>

        {/* Featured Badge */}
        {vehicle.featured && (
          <div className="absolute top-5 right-5">
            <span className={`px-4 py-1.5 text-white text-xs font-semibold uppercase tracking-wider ${
              isDark ? 'bg-blue-600/90' : 'bg-red-600/90'
            }`}>
              {t('featured.badge')}
            </span>
          </div>
        )}

        {/* Price Overlay */}
        <div className="absolute bottom-5 right-5 text-right">
          {formatVehiclePrice(vehicle) ? (
            <span className="font-unbounded text-3xl font-bold text-white drop-shadow-lg">
              {formatVehiclePrice(vehicle)}
            </span>
          ) : (
            <span className="inline-block px-3 py-1.5 bg-black/55 backdrop-blur-sm font-unbounded text-sm md:text-base font-semibold text-white drop-shadow-lg uppercase tracking-wide">
              {t('inventory.getQuote')}
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Make */}
        <span className={`text-sm uppercase tracking-widest font-medium ${
          isDark ? 'text-blue-500' : 'text-red-600'
        }`}>
          {vehicle.make}
        </span>
        
        {/* Model & Year */}
        <h3 className={`font-unbounded text-xl font-semibold mt-2 transition-colors ${
          isDark 
            ? 'text-white group-hover:text-blue-400' 
            : 'text-gray-900 group-hover:text-red-600'
        }`}>
          {vehicle.year} {vehicle.model}{vehicle.trim ? ` ${vehicle.trim}` : ''}
        </h3>

        {/* Specs */}
        <div className={`flex items-center gap-6 mt-5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          <div className="flex items-center gap-2">
            <Gauge className="h-5 w-5" />
            <span className="text-base">{vehicle.mileage.toLocaleString()} mi</span>
          </div>
          <div className="flex items-center gap-2">
            <Fuel className="h-5 w-5" />
            <span className="text-base">{displayFuelType}</span>
          </div>
        </div>

        {/* Divider */}
        <div className={`border-t mt-5 pt-5 ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between">
            {/* Compare Checkbox */}
            {showCompare && (
              <button
                onClick={handleCheckboxClick}
                className={`flex items-center gap-2 px-3 py-1.5 border transition-all ${
                  isSelected
                    ? isDark 
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : 'bg-red-600 border-red-600 text-white'
                    : isDark 
                      ? 'bg-transparent border-white/20 text-gray-400 hover:border-blue-500/50 hover:text-white'
                      : 'bg-transparent border-gray-300 text-gray-600 hover:border-red-400 hover:text-gray-900'
                }`}
                data-testid={`compare-checkbox-${vehicle.id}`}
              >
                <div className={`w-4 h-4 border flex items-center justify-center transition-all ${
                  isSelected 
                    ? 'bg-white border-white' 
                    : 'border-current'
                }`}>
                  {isSelected && <Check className={`w-3 h-3 ${isDark ? 'text-blue-600' : 'text-red-600'}`} />}
                </div>
                <span className="text-xs font-medium uppercase tracking-wider">
                  {isSelected ? t('featured.selected') : t('featured.compare')}
                </span>
              </button>
            )}
            
            {!showCompare && (
              <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>
                {displayExteriorColor}
              </span>
            )}
            
            <span className={`font-medium group-hover:underline ${
              isDark ? 'text-blue-500' : 'text-red-600'
            }`}>
              {t('featured.viewDetails')} →
            </span>
          </div>
        </div>
      </div>

      {/* Selection Indicator */}
      {isSelected && (
        <div className="absolute top-5 right-5 z-10">
          <div className={`w-8 h-8 flex items-center justify-center ${
            isDark ? 'bg-blue-600' : 'bg-red-600'
          }`}>
            <Check className="w-5 h-5 text-white" />
          </div>
        </div>
      )}

      {/* Hover Glow Effect */}
      <div className={`absolute inset-0 transition-opacity duration-300 pointer-events-none ${
        isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
      }`}>
        <div className={`absolute inset-0 ${
          isDark 
            ? 'shadow-[inset_0_0_40px_rgba(59,130,246,0.15)]' 
            : 'shadow-[inset_0_0_40px_rgba(220,38,38,0.1)]'
        }`} />
      </div>
    </div>
  );
}
